import React, { useState, useEffect, useContext } from "react";
import { useParams } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import Loader from "../Components/Shared/Loader/loader";
import { useTranslation } from "react-i18next";
import { getLoggedInUserUsingAccessToken } from "../API/User/User";
import { getUserAndItsOrganizations } from "../API/Organization/organization";
import { GlobalContext } from "./GlobalContext";

export const WorkspaceOrganizationRouteRender = (props) => {
	const { organizationId } = useParams();
	const { inProgress } = useMsal(); // Destructure instance & inProgress for clarity
	const { i18n, ready: tReady } = useTranslation();
	const { dispatch } = useContext(GlobalContext)

	const [loading, setLoading] = useState(true);
	const [hasAccess, setHasAccess] = useState(false);
	const [accessError, setAccessError] = useState("");
	const [loggedInUser, setLoggedInUser] = useState(null);
	const [organizationData, setOrganizationData] = useState({
		organization: {},
		user: {},
		role: {}
	});

	// Fetch logged-in user once when the component mounts
	useEffect(() => {
		const accessToken = localStorage.getItem("accessToken");

		if (!accessToken) {
			setLoading(false);
			setHasAccess(false);
			setAccessError("Sorry, Please login to continue.");
			return;
		}

		// If inProgress is not "none", avoid running the effect
		if (inProgress !== "none") return;

		const fetchUser = async () => {
			try {
				const user = await getLoggedInUserUsingAccessToken();
				setLoggedInUser(user);
			} catch (error) {
				setAccessError("Error fetching user data.");
				setLoading(false);
			}
		};

		fetchUser();
	}, [inProgress]);

	// Validate access to the organization after the user is fetched
	useEffect(() => {
		if (!loggedInUser) return; // Wait until loggedInUser is available

		const validateAccess = async () => {
			setLoading(true);
			try {
				const { organizations, user } = await getUserAndItsOrganizations(loggedInUser.emails[0]);
				const organization = organizations.find(org => org._id === organizationId);

				if (!organization) {
					setHasAccess(false);
					setAccessError("Sorry. You have not been granted access to this Organization. Please contact the Admin(s).");
				} else {
					const organizationUser = organization.users.find(u => u.userId === user._id);
					const role = organization.roles.find(role => role._id === organizationUser.roleId);

					setOrganizationData({ organization, user, role });
					setHasAccess(true);
					dispatch({ type: "SET_ORGANIZATION", payload: organization })
					dispatch({ type: "SET_ORGANIZATIONS", payload: organizations })
					dispatch({ type: "SET_USER", payload: user })
				}
			} catch (error) {
				setAccessError("Error validating access.");
			} finally {
				setLoading(false);
			}
		};

		validateAccess();
	}, [loggedInUser, organizationId]);

	// Update the language when organization changes
	useEffect(() => {
		if (tReady && organizationData.organization) {
			const platformLanguage = organizationData.organization.platformLanguage || "en";
			i18n.changeLanguage(platformLanguage);
		}
	}, [tReady, organizationData.organization, i18n]);

	if (loading) return <Loader />;

	if (!hasAccess) {
		return (
			<div className="container align-items-center" style={{ height: "100vh" }}>
				<div className="row justify-content-center">
					<div className="text-center">
						<img
							width={200}
							alt="Progress-Loader"
							src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/Error.png`}
						/>
						<div className="font-size-2 mx-5 px-5">{accessError}</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<React.Fragment>
			{React.Children.map(props.children, (child) =>
				React.isValidElement(child)
					? React.cloneElement(child, {
						organization: organizationData.organization,
						user: organizationData.user,
						role: organizationData.role,
					})
					: child
			)}
		</React.Fragment>
	)
}
